import axios from "axios";

import { searchLiterature } from "./researchApi";
import { LiteratureResult } from "../../types/research/ResearchTypes";

// One corpus read per catalogue search — enough to verify the page's citations and to read around the trait.
const LITERATURE_SIZE = 100;

// A STRING discriminant, not `available: boolean`: this repo compiles with `strict: false`, and
// without strictNullChecks TypeScript does not narrow a union on a boolean literal.
export type LiteratureStatus = "ok" | "forbidden" | "unavailable";

export type LiteratureEvidence =
	| { status: "ok"; pmids: string[]; results: LiteratureResult[]; total: number }
	| { status: "forbidden" | "unavailable"; message: string };

export const FORBIDDEN_MESSAGE = "Your account cannot reach the research service, so citations cannot be checked. Catalogue results are still shown, without evidence links.";

export const UNAVAILABLE_MESSAGE = "The research service could not be reached, so citations cannot be checked. Catalogue results are still shown, without evidence links.";

/**
 * The literature half, FAIL CLOSED (D4/Q2). Three outcomes collapse into one rendered `unavailable`
 * state and NEVER into an empty result set: a 403 (the literature routes admit `access-practitioner-system`
 * or `access-gene.codes`, so only a token holding neither lands here),
 * a degraded connector's own `available: false` 200, and every transport failure — 503, 429, timeout.
 * Returning rather than throwing is the handling: "we could not check" is a thing the pane must say.
 */
export const readLiteratureForTrait = async (term: string): Promise<LiteratureEvidence> => {
	try {
		const response = await searchLiterature({ q: term, size: LITERATURE_SIZE, record: false });

		if (!response.available) {
			return { status: "unavailable", message: "message" in response ? response.message : UNAVAILABLE_MESSAGE };
		}

		return {
			status: "ok",
			pmids: response.data.map((result) => result.meta?.pmid).filter((pmid): pmid is string => typeof pmid === "string" && pmid !== ""),
			results: response.data,
			total: response.total,
		};
	} catch (error) {
		if (axios.isAxiosError(error) && error.response?.status === 403) {
			return { status: "forbidden", message: FORBIDDEN_MESSAGE };
		}
		return { status: "unavailable", message: UNAVAILABLE_MESSAGE };
	}
};
